import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { BootcampPageUrlParamsType, LevelActionType } from '~/bootcamp/types/bootcamp.types'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import {
  resetCorrectConsecutiveAnswersActionCreator,
  selectAnswerState,
  selectBootcampActivityEvents,
  selectCorrectConsecutiveAnswers,
  setAnswerStateActionCreator,
  setIsRewardFeedbackScreenOpenActionCreator,
} from '~/bootcamp/slices/bootcamp-activity.slice'
import QuizWrapper from '~/bootcamp/components/Quiz/QuizWrapper'
import {
  selectBootcampPageActivities,
  selectBootcampPageCompleted,
  selectBootcampPageIsEnglishOnly,
  selectBootcampPageSectionName,
} from '~/bootcamp/slices/bootcamp-page.slice'
import { selectVideoTopicColor } from '~/bootcamp/slices/vcc-data.slice'
import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'
import useShowRoundSummary from '~/bootcamp/hooks/useShowRoundSummary'
import RewardFeedbackScreen from '~/bootcamp/pages/BootcampView/BootcampContent/RewardFeedbackScreen'
import useGetAvailableRewards from '~/bootcamp/hooks/useGetAvailableRewards'
import useGetCollectedRewards from '~/bootcamp/hooks/useGetCollectedRewards'
import { useGetLeagueInfoForNav } from '~/hooks/useGetLeagueInfo'
import {
  selectLeaderboardPosition,
  setHasShownLeaderboardModal,
  setLeaderboardPosition,
} from '~/gamification/slices/gamification.slice'
import LeagueModal from '~/bootcamp/pages/BootcampView/BootcampContent/LeagueModal'
import { leaderboardApi } from '~/Leaderboard/apis/leaderboard-api'
import { rewardApi } from '~/redux/apis/reward-api'
import { USER_API_TAGS } from '~/redux/constants/tags.const'
import ActivitiesWrapper from '~/bootcamp/pages/BootcampView/BootcampContent/ActivitiesWrapper'
import CorrectAnswerScreen from '~/bootcamp/pages/BootcampView/BootcampContent/CorrectAnswerScreen'
import RewardChestScreen from '~/bootcamp/pages/BootcampView/BootcampContent/RewardChestScreen'
import VideoContentWrapper from '~/bootcamp/pages/BootcampView/BootcampContent/VideoContentWrapper'

export default function ContentSection(): React.ReactElement {
  const [showRewardScreen, setShowRewardScreen] = useState(false)
  const [startAnimation, setStartAnimation] = useState(false)
  const [resetFeedback, setResetFeedback] = useState(false)
  const [showLeagueModal, setShowLeagueModal] = useState(false)
  const router = useRouter()
  const { t } = useTranslation('bootcamp')
  const { query } = router
  const { action, key: bootcampKey, moduleKey, subtopic } = query as unknown as BootcampPageUrlParamsType
  const isLongDivision = bootcampKey === 'division' && moduleKey === '2'
  const isCompleted = useAppSelector(selectBootcampPageCompleted)
  const sectionName = useAppSelector(selectBootcampPageSectionName)
  const isEnglishOnly = useAppSelector(selectBootcampPageIsEnglishOnly)
  const answerState = useAppSelector(selectAnswerState)
  const dispatch = useAppDispatch()
  const colorInfo: CategoryColorClasses = useAppSelector(selectVideoTopicColor)
  const { userCurrentRank } = useGetLeagueInfoForNav()
  const userPreviousRank = useAppSelector(selectLeaderboardPosition)
  const showRoundSummary = useShowRoundSummary()

  const hasLeagueData = Boolean(userCurrentRank && userPreviousRank)
  const isEndOfActivitiesSet = showLeagueModal && showRoundSummary

  const activities = useAppSelector(selectBootcampPageActivities) || []
  const events = useAppSelector(selectBootcampActivityEvents)
  const collectedRewards = useGetCollectedRewards(events)
  const availableRewards = useGetAvailableRewards(activities)
  const isLastRewardInSet = collectedRewards + 1 === availableRewards
  const correctConsecutiveAnswers = useAppSelector(selectCorrectConsecutiveAnswers)

  useEffect(() => {
    if (correctConsecutiveAnswers === 3) {
      setShowLeagueModal(true)
      setShowRewardScreen(true)
      setStartAnimation(true)
      dispatch(setIsRewardFeedbackScreenOpenActionCreator(true))
      const timeout = setTimeout(() => {
        dispatch(setIsRewardFeedbackScreenOpenActionCreator(false))
      }, 3000)
      const timeout2 = setTimeout(() => {
        setShowRewardScreen(false)
        setStartAnimation(false)
        dispatch(setIsRewardFeedbackScreenOpenActionCreator(false))
      }, 4500)
      return () => {
        clearTimeout(timeout)
        clearTimeout(timeout2)

        // To reset the state if the user moves to the next question before the timeout
        setShowRewardScreen(false)
        setStartAnimation(false)
        setResetFeedback(true)
        dispatch(setIsRewardFeedbackScreenOpenActionCreator(false))
      }
    }
  }, [correctConsecutiveAnswers, dispatch])

  useEffect(() => {
    if (isLastRewardInSet) {
      dispatch(rewardApi.util.invalidateTags(['Rewards']))
      dispatch(leaderboardApi.util.invalidateTags([USER_API_TAGS.LeagueParticipation]))
    }
  }, [dispatch, isLastRewardInSet])

  useEffect(() => {
    dispatch(resetCorrectConsecutiveAnswersActionCreator()) // Cleared all consecutive answer history
    dispatch(setAnswerStateActionCreator(null)) // Reset answer state, null means not attempted.
  }, [dispatch])

  useEffect(() => {
    if (!userPreviousRank || userCurrentRank > userPreviousRank) {
      dispatch(setLeaderboardPosition(userCurrentRank))
    }
  }, [dispatch, userCurrentRank, userPreviousRank])

  useEffect(() => {
    if (resetFeedback) {
      const timeout = setTimeout(() => {
        setResetFeedback(false)
      }, 100)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [resetFeedback])

  const onCloseLeagueModal = (): void => {
    setShowLeagueModal(false)
    dispatch(setLeaderboardPosition(userCurrentRank))
    dispatch(setHasShownLeaderboardModal(true))
  }

  const activityComponent = (activity: string): React.ReactElement => {
    switch (activity) {
      case LevelActionType.Activity:
        return <ActivitiesWrapper />
      case LevelActionType.Quiz:
        return (
          <QuizWrapper bootcampKey={bootcampKey} isCompleted={isCompleted} moduleKey={moduleKey} subtopic={subtopic} />
        )
      default:
        return <VideoContentWrapper />
    }
  }

  const heading =
    action !== LevelActionType.Activity && sectionName ? (
      <h2
        className={`font-semibold text-md leading-6 my-4 tracking-wider ${colorInfo.textClass}`}
        data-testid="content-header"
        id="gtag-video-title" // ID for tracking video title on Google Analytics via Google Tag Manager
      >
        {sectionName}
      </h2>
    ) : null
  return (
    <Fragment>
      {answerState && correctConsecutiveAnswers !== 3 && <CorrectAnswerScreen />}
      {showRewardScreen && isLastRewardInSet && <RewardChestScreen />}
      {!resetFeedback && (
        <div
          className={`duration-1000 ${
            startAnimation && !isLastRewardInSet ? 'opacity-100 z-modal' : '-z-10 pointer-events-none opacity-0'
          }`}
        >
          <RewardFeedbackScreen setStartAnimation={setStartAnimation} />
        </div>
      )}
      {hasLeagueData && isEndOfActivitiesSet && (
        <LeagueModal
          currentPos={userCurrentRank}
          onCloseLeagueModal={onCloseLeagueModal}
          prevPos={userPreviousRank || 0}
        />
      )}

      <div className="flex-grow flex flex-col z-20">
        {isEnglishOnly && (
          <article className="w-full px-8 pt-2 md:px-0 self-center">
            <div className="p-2 bg-bootcamp-notification-gradient rounded border border-white shadow-md text-xs font-semibold text-white">
              {isLongDivision ? t('engVideoTagLongDivision') : t('engVideoTag')}
            </div>
          </article>
        )}
        {action === LevelActionType.Encyclopedia || action === LevelActionType.Quiz || showRoundSummary
          ? null
          : heading}
        {activityComponent(action)}
      </div>
    </Fragment>
  )
}
